import { useCallback,useEffect, useRef, useState } from "react";

const visibleCountCache = new Map();

export const useOverflowDetection = (totalCount, enabled = true, cacheKey) => {
  const getInitialVisibleCount = () => {
    if (cacheKey && visibleCountCache.has(cacheKey)) {
      const cached = visibleCountCache.get(cacheKey);
      if (cached <= totalCount && cached >= 1) return cached;
    }
    return totalCount;
  };

  const containerRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(getInitialVisibleCount);
  const rafRef = useRef(null);
  const lastWidthRef = useRef(0);
  const prevTotalCountRef = useRef(totalCount);
  const stableVisibleCountRef = useRef(totalCount);

  const tabsRef = useRef(null);

  const checkOverflow = useCallback(() => {
    if (!enabled || !containerRef.current || totalCount <= 1) {
      if (enabled && totalCount > 0) {
        setVisibleCount(totalCount);
        stableVisibleCountRef.current = totalCount;
      }
      return;
    }

    const tabsEl = tabsRef.current;
    if (!tabsEl) return;

    const availableWidth = tabsEl.clientWidth;
    const contentWidth = tabsEl.scrollWidth;
    const containerWidth = containerRef.current.clientWidth;
    const containerGrew = containerWidth > lastWidthRef.current;
    lastWidthRef.current = containerWidth;

    setVisibleCount((prev) => {
      let next = prev;
      if (contentWidth > availableWidth && prev > 1) {
        const overflow = contentWidth - availableWidth;
        const avgItemWidth = contentWidth / prev;
        const itemsToHide = Math.min(
          prev - 1,
          Math.max(1, Math.ceil(overflow / (avgItemWidth || 1)))
        );
        next = Math.max(1, prev - itemsToHide);
      } else if (
        contentWidth <= availableWidth &&
        prev < totalCount &&
        containerGrew
      ) {
        next = prev + 1;
      }
      if (next !== prev) {
        stableVisibleCountRef.current = next;
        if (cacheKey) visibleCountCache.set(cacheKey, next);
      }
      return next;
    });
  }, [enabled, totalCount, cacheKey]);

  useEffect(() => {
    if (!enabled || totalCount <= 0) {
      setVisibleCount(totalCount);
      prevTotalCountRef.current = totalCount;
      stableVisibleCountRef.current = totalCount;
      return;
    }
    if (prevTotalCountRef.current !== totalCount) {
      const prevTotal = prevTotalCountRef.current;
      prevTotalCountRef.current = totalCount;
      if (totalCount > prevTotal) {
        setVisibleCount(totalCount);
        stableVisibleCountRef.current = totalCount;
        if (cacheKey) visibleCountCache.set(cacheKey, totalCount);
      } else {
        const clamped = Math.min(
          Math.max(1, stableVisibleCountRef.current),
          totalCount
        );
        setVisibleCount(clamped);
        stableVisibleCountRef.current = clamped;
        if (cacheKey) visibleCountCache.set(cacheKey, clamped);
      }
    }
  }, [enabled, totalCount, cacheKey]);

  useEffect(() => {
    if (!enabled || !containerRef.current) return;

    if (containerRef.current) {
      lastWidthRef.current = containerRef.current.clientWidth;
    }

    const resizeObserver = new ResizeObserver(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        checkOverflow();
        rafRef.current = null;
      });
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled, checkOverflow]);

  useEffect(() => {
    if (!enabled) return;
    const timer = setTimeout(() => checkOverflow(), 100);
    return () => clearTimeout(timer);
  }, [enabled, visibleCount, checkOverflow]);

  const hasOverflow = enabled && visibleCount < totalCount && totalCount > 0;

  return {
    containerRef,
    tabsRef,
    visibleCount: Math.max(1, Math.min(visibleCount, totalCount)),
    hasOverflow,
  };
};
